import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSurveyStore } from '@/stores/survey-store';
import { useSessionStore } from '@/stores/session-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { SurveyLayout } from '@/components/layout/survey-layout';
import { LoadingScreen } from '@/components/screens/loading-screen';
import { ErrorScreen } from '@/components/screens/error-screen';

interface PreviewData {
  surveyId: string;
  survey: {
    id: string;
    name: string;
    description: string;
    state: string;
    settings: Record<string, unknown>;
    theme: Record<string, unknown>;
  };
  questions: Array<{
    id: string;
    code: string;
    text: string;
    type: string;
    required: boolean;
    order: number;
    config?: Record<string, unknown>;
  }>;
  blocks: Array<{
    id: string;
    name: string;
    order: number;
  }>;
  timestamp: number;
}

export function PreviewPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setSurveyData = useSurveyStore((state) => state.setSurveyData);
  const survey = useSurveyStore((state) => state.survey);
  const setTotalPages = useNavigationStore((state) => state.setTotalPages);
  const goToPage = useNavigationStore((state) => state.goToPage);

  // Initialize preview mode from localStorage
  useEffect(() => {
    async function initializePreview() {
      if (!surveyId) {
        setError('Survey ID is required');
        setIsInitializing(false);
        return;
      }

      try {
        // Read preview data from URL hash (base64 encoded)
        const hash = window.location.hash.slice(1); // Remove the # prefix

        if (!hash) {
          setError('No preview data found. Please click Preview from Survey Studio.');
          setIsInitializing(false);
          return;
        }

        // Decode base64 and parse JSON
        const decodedData = decodeURIComponent(atob(hash));
        const previewData: PreviewData = JSON.parse(decodedData);

        // Verify the surveyId matches
        if (previewData.surveyId !== surveyId) {
          setError('Preview data mismatch. Please click Preview from Survey Studio again.');
          setIsInitializing(false);
          return;
        }

        // Check if data is stale (older than 1 hour)
        const ONE_HOUR = 60 * 60 * 1000;
        if (Date.now() - previewData.timestamp > ONE_HOUR) {
          setError('Preview data expired. Please click Preview from Survey Studio again.');
          setIsInitializing(false);
          return;
        }

        // Set up survey data
        setSurveyData({
          survey: previewData.survey as any,
          questions: previewData.questions as any,
          logic: [],
          pages: [{ id: 'page-1', order: 1, title: 'Page 1' }],
          versionId: 'preview',
        });

        // Initialize navigation
        setTotalPages(1);
        goToPage(1);

        // Mark session as active for preview
        useSessionStore.setState({
          sessionId: `preview-${surveyId}`,
          isActive: true,
          isStarted: true,
          isEnded: false,
          startedAt: new Date(),
        });

        setIsInitializing(false);
      } catch (err) {
        console.error('Failed to load preview:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load survey preview.'
        );
        setIsInitializing(false);
      }
    }

    initializePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // Show loading screen
  if (isInitializing) {
    return <LoadingScreen />;
  }

  // Show error screen
  if (error) {
    return <ErrorScreen message={error} />;
  }

  // Show survey layout with questions
  if (survey) {
    return (
      <div className="relative">
        {/* Preview Banner */}
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-center py-2 text-sm font-medium z-50 shadow-md">
          Preview Mode - Responses will not be saved
        </div>
        <div className="pt-10">
          <SurveyLayout />
        </div>
      </div>
    );
  }

  // Fallback loading
  return <LoadingScreen />;
}
